import type { AdminPeriodRange } from '@/composables/useAdminPeriod'
import { adminService } from '@/services/AdminService'
import { ServiceError } from '@/services/util/ServiceError'
import type {
  AdminDashboardData,
  AdminDashboardSeries,
  AdminDashboardTotals,
  ChartDisplayMode,
  ChartGranularity
} from '@/utils/adminDashboardUtils'
import { resolveGranularityFromPeriod } from '@/utils/adminDashboardUtils'
import { ref, watch, type Ref } from 'vue'

const emptyTotals = (): AdminDashboardTotals => ({
  companies: 0,
  employees: 0,
  deliveryNotes: 0,
  aiRequests: 0
})

const emptySeries = (): AdminDashboardSeries => ({
  companies: [],
  employees: [],
  deliveryNotes: [],
  aiRequests: []
})

export function useAdminDashboard(
  periodRange: Ref<AdminPeriodRange>,
  periodDurationDays: Ref<number>
) {
  const loading = ref(false)
  const totals = ref<AdminDashboardTotals>(emptyTotals())
  const series = ref<AdminDashboardSeries>(emptySeries())
  const chartDisplayMode = ref<ChartDisplayMode>('period')
  const chartGranularity = ref<ChartGranularity>('day')

  const loadDashboard = async () => {
    loading.value = true
    chartGranularity.value = resolveGranularityFromPeriod(periodRange.value, periodDurationDays.value)

    const response = await adminService.getDashboard(
      periodRange.value.startDate,
      periodRange.value.endDate,
      chartGranularity.value
    )

    if (response instanceof ServiceError) {
      totals.value = emptyTotals()
      series.value = emptySeries()
      loading.value = false
      return
    }

    totals.value = response.totals
    series.value = response.series
    loading.value = false
  }

  watch(
    [periodRange, periodDurationDays],
    () => {
      loadDashboard()
    },
    { immediate: true, deep: true }
  )

  return {
    loading,
    totals,
    series,
    chartDisplayMode,
    chartGranularity,
    loadDashboard
  }
}

export type { AdminDashboardData }
